import React from 'react';
import { useSelector } from 'react-redux';
import { array, string, func, bool } from 'prop-types';
import { ModalStyled, Box, Stack, Grid, ListStyled } from '@nike/nike-design-system-components';

import ProductImage from '~COMPONENTS/aurora/ProductImage';
import { TranslatedCopy } from '../../intl/TranslatedCopy';
import PdpPrice from './PdpPrice';
import LoadingAnimation from '../../components/LoadingAnimation';

import { getProductDetailsFromId, getIsFetchingStyleDetails } from '../selectors';

const PdpProductDetailsModal = ({ selectedProductPrices, productCode, isOpen, closeModal }) => {
  const productInfo = useSelector(state => getProductDetailsFromId(state, productCode));
  const isFetching = useSelector(getIsFetchingStyleDetails);
  const { description, globalColorName, name, productDetails, productFeatures, productSummary, reasonToBuy } =
    productInfo || {};

  return (
    <ModalStyled
      isOpen={isOpen}
      closeModal={closeModal}
      title=''
      modalRootCompProps={{ style: { maxWidth: '1000px' } }}
      modalHeaderCompProps={{ style: { paddingBottom: '0px' } }}
      slots={{
        HeaderTitleSlot: productInfo && (
          <Box style={{ fontSize: '16px' }} data-testId='productDetailsHeader'>
            <Grid style={{ padding: 0 }}>
              <Box width='50px' height='50px'>
                <ProductImage productCode={productCode} alt={productCode} width={50} height={50} />
              </Box>
              <Box paddingInlineStart='10px'>
                <Stack>
                  <span>{name}</span>
                  <PdpPrice selectedProductPrices={selectedProductPrices} />
                </Stack>
              </Box>
            </Grid>
          </Box>
        )
      }}
    >
      <>
        {productInfo ? (
          <Box paddingInlineStart='5px' data-testId='productDetails'>
            <Stack style={{ fontSize: '15px' }}>
              <ul style={{ listStyle: 'disc', padding: '0 0 12px 27px', lineHeight: '1.25' }}>
                <li>
                  <TranslatedCopy id='global.product.style' />
                  {': '}
                  {productCode}
                </li>
                <li>
                  <TranslatedCopy id='global.color' />
                  {': '}
                  {globalColorName}
                </li>
              </ul>
            </Stack>
            {reasonToBuy && (
              <Box padding='12px 0' style={{ fontWeight: '500' }}>
                {reasonToBuy}
              </Box>
            )}
            <Box padding='12px 0'>{productSummary || description}</Box>
            {productFeatures && productFeatures.length > 0 && (
              <Box padding='12px 0'>
                <Box style={{ fontWeight: '500' }}>
                  <TranslatedCopy id='pdp.description.productDetails.benefits' />
                </Box>
                <ListStyled listStyle='bulleted'>
                  {productFeatures.map(({ description }) => (
                    <li>{description}</li>
                  ))}
                </ListStyled>
              </Box>
            )}
            {productDetails && productDetails.length > 0 && (
              <Box padding='10px 0'>
                <Box style={{ fontWeight: '500' }}>
                  <TranslatedCopy id='pdp.description.productDetails' />
                </Box>
                <ListStyled listStyle='bulleted'>
                  {productDetails.map(({ description }) => (
                    <li>{description}</li>
                  ))}
                </ListStyled>
              </Box>
            )}
          </Box>
        ) : (
          isFetching && (
            <div
              data-testid='spinner'
              css={{ left: '50%', top: '50%', position: 'absolute', transform: 'translate(-50%, -50%)' }}
            >
              <LoadingAnimation size='s' />
            </div>
          )
        )}
      </>
    </ModalStyled>
  );
};

PdpProductDetailsModal.propTypes = {
  selectedProductPrices: array,
  productCode: string,
  isOpen: bool.isRequired,
  closeModal: func.isRequired
};

PdpProductDetailsModal.defaultProps = {
  selectedProductPrices: [],
  productCode: ''
};

export default PdpProductDetailsModal;
